import { Button } from '@mantine/core';
import { Icon, sizes, variants } from '@uag/react-core';
import { t as translate } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import { useKeyboardLayout } from 'app/modules/wallforming/components/input-binding/hooks/useGetInputActionLabel';
import { inputActions } from 'app/modules/wallforming/components/input-binding/registry/inputActionsRegistry';
import { formatChordLabel } from 'app/modules/wallforming/components/input-binding/utils/inputChordUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { InputBindingDialog as InputBindingDialogState } from 'app/modules/wallforming/components/input-binding/types/inputBindingDialog';
import type { KeyboardLayoutMap } from 'app/modules/wallforming/components/input-binding/types/keyboardLayoutMap';

function getDialogContent(dialog: InputBindingDialogState, keyboardLayout?: KeyboardLayoutMap) {
    switch (dialog.kind) {
        case 'conflict':
            return {
                message: translate('infoKeyBindingConflict', {
                    combo: formatChordLabel(dialog.chord, keyboardLayout),
                    action: dialog.conflicts.map((actionId) => translate(inputActions[actionId].labelKey)).join(', ')
                }),
                confirmLabel: translate('labelKeyBindingOverwrite')
            };
        case 'readOnlyProfile':
            return {
                message: translate('infoKeyBindingProfileReadOnly'),
                confirmLabel: translate('labelKeyBindingDuplicateProfile')
            };
        default:
            return dialog satisfies never;
    }
}

/** Confirmation overlay rendered inside the binding panel. */
export const InputBindingDialog = () => {
    const { t } = useTranslation();
    const keyboardLayout = useKeyboardLayout();
    const { dialog, confirm, close } = usePlacementStore(
        useShallow((state) => ({
            dialog: state.inputBindingDialog,
            confirm: state.confirmInputBindingDialog,
            close: state.closeInputBindingDialog
        }))
    );

    if (!dialog) {
        return null;
    }
    const { message, confirmLabel } = getDialogContent(dialog, keyboardLayout);

    return (
        <div
            className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-black/20"
            role="presentation"
        >
            <div
                className="flex max-w-md flex-col gap-3 rounded-md border border-amber-300 bg-background-default p-4 text-xs shadow-lg"
                role="alertdialog"
                aria-modal
            >
                <div className="flex items-start gap-2 text-amber-900">
                    <Icon className="icon-sm" icon="warning" />
                    <span>{message}</span>
                </div>
                <div className="flex justify-end gap-2">
                    <Button size={sizes.button.small} variant={variants.button.secondary} onClick={close}>
                        {t('cancel')}
                    </Button>
                    <Button size={sizes.button.small} onClick={confirm}>
                        {confirmLabel}
                    </Button>
                </div>
            </div>
        </div>
    );
};
